// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useEffect, useState, ReactNode } from "react";
import { onAuthStateChanged, getIdTokenResult, User } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";

interface Props {
  children: ReactNode;
  /** Client-level roles allowed (receptionist | manager | super_admin) */
  allowedRoles?: string[];
  /** Set true to allow only super_admin platform claim */
  platformAdmin?: boolean;
}

export function RequireAuth({
  children,
  allowedRoles = [],
  platformAdmin = false,
}: Props) {
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u: User | null) => {
      if (!u) {
        navigate("/login");
        return;
      }
      const result = await getIdTokenResult(u, true);
      const claims = result.claims as Record<string, unknown>;
      const isPlatformAdmin = claims.platformRole === "super_admin";
      const role = (claims.role as string) ?? "";

      const ok =
        (platformAdmin && isPlatformAdmin) ||
        (!platformAdmin && allowedRoles.includes(role));

      if (!ok) {
        navigate("/login");
        return;
      }
      setAuthorized(true);
      setLoading(false);
    });
    return () => unsub();
  }, [allowedRoles, platformAdmin, navigate]);

  if (loading || !authorized) return <div>Loading…</div>;
  return <>{children}</>;
}
